/**
 * Safe area utility
 *
 * Reads safe area insets from CSS env() variables and applies overrides
 * received from the KMP host bridge (which provides pixel values explicitly).
 */

import type { SafeAreaInsets } from '@/bridge/bridge-types';

/**
 * Read current safe area insets from CSS.
 * Works in browsers that support env() — returns 0 for unsupported ones.
 */
export function readSafeAreaFromCss(): SafeAreaInsets {
  const getInt = (prop: string): number => {
    const value = getComputedStyle(document.documentElement).getPropertyValue(prop).trim();
    return parseInt(value, 10) || 0;
  };

  return {
    top: getInt('--safe-top'),
    right: getInt('--safe-right'),
    bottom: getInt('--safe-bottom'),
    left: getInt('--safe-left'),
  };
}

/**
 * Apply explicit safe area values (from KMP bridge) as CSS variables.
 * Overrides the env() defaults.
 */
export function applySafeArea(insets: SafeAreaInsets): void {
  const root = document.documentElement;
  root.style.setProperty('--safe-top', `${insets.top}px`);
  root.style.setProperty('--safe-right', `${insets.right}px`);
  root.style.setProperty('--safe-bottom', `${insets.bottom}px`);
  root.style.setProperty('--safe-left', `${insets.left}px`);
}
